import React from "react";
import {
  Typography,
  Fab,
  CircularProgress,
  Badge,
  Box,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import {
  Add as AddIcon,
  DisplaySettings as DisplaySettingsIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { MonitorGroup, ServiceStatusAggregation } from "../../Models/Types";
import "./GroupsSection.scss";

var LOCK_MODAL_CALLBACK_TWEAK = false;

interface GroupsSectionProps {
  groups: MonitorGroup[];
  loading: boolean;
  onOpenModal: (group?: MonitorGroup) => void;
  onSelectGroup: (group: MonitorGroup) => void;
  // We need the aggregations to calculate how many *Apps* are in a group
  allAggregations: ServiceStatusAggregation[];
}

const GroupsSection: React.FC<GroupsSectionProps> = ({
  groups,
  loading,
  onOpenModal,
  onSelectGroup,
  allAggregations,
}) => {
  const handleGroupClick = (group: MonitorGroup) => {
    setTimeout(() => {
      if (LOCK_MODAL_CALLBACK_TWEAK) return;
      onSelectGroup(group);
      window.scrollTo(0, 0);
    }, 100);
  };

  const handleEditClick = (e: React.MouseEvent, group: MonitorGroup) => {
    e.stopPropagation();
    LOCK_MODAL_CALLBACK_TWEAK = true;
    setTimeout(() => {
      LOCK_MODAL_CALLBACK_TWEAK = false;
    }, 500);
    onOpenModal(group);
  };

  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOpenModal();
  };

  const getGroupKoCount = (group: MonitorGroup): number => {
    if (!group.applicationIdsInGroup) return 0;

    const appIds = group.applicationIdsInGroup
      .split(",")
      .map((id) => parseInt(id.trim()));

    const koCount = allAggregations.filter(
      (agg) =>
        appIds.includes(agg.application_id) && agg.aggregate_status === false
    ).length;

    return koCount;
  };

  if (loading)
    return (
      <div className="section-container bg-white">
        <CircularProgress />
      </div>
    );

  return (
    <div className="section-container bg-white">
      <Accordion defaultExpanded={true} elevation={0} sx={{ "&:before": { display: "none" } }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel-groups-content"
          id="panel-groups-header"
          sx={{ padding: 0 }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              pr: 2,
            }}
          >
            <Typography className="section-title" sx={{ mb: 0 }}>
              I miei gruppi
            </Typography>
            <Fab
              size="small"
              aria-label="add"
              onClick={handleAddClick}
              sx={{ boxShadow: "none", ml: 2, zIndex: 1 }}
            >
              <AddIcon />
            </Fab>
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ padding: "8px 0" }}>
          {groups.length === 0 && (
            <Typography align="center" color="text.secondary" sx={{ mt: 2 }}>
              Non sono presenti gruppi definiti dall'utente.
            </Typography>
          )}

          <div className="groups-grid">
            {groups.map((group) => {
              const koCount = getGroupKoCount(group);
              const appCount = group.applicationIdsInGroup
                ? group.applicationIdsInGroup.split(",").length
                : 0;

              return (
                <div
                  key={group.id}
                  className="group-card"
                  onClick={() => handleGroupClick(group)}
                  style={{ position: "relative" }} // Ensure relative positioning for the absolute button
                >
                  {/* Edit Button - Pinned to Top Right */}
                  <div
                    style={{
                      position: "absolute",
                      top: 4,
                      left: 4,
                      zIndex: 10,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => handleEditClick(e, group)}
                      sx={{
                        bgcolor: "white",
                        "&:hover": { bgcolor: "#f5f5f5" },
                        boxShadow: 1,
                      }}
                    >
                      <EditIcon fontSize="small" color="action" />
                    </IconButton>
                  </div>

                  {/* Card Content */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      pt: 2, // Padding top to clear space (though button is absolute)
                      pb: 1,
                      width: "100%",
                    }}
                  >
                    <Badge
                      badgeContent={appCount}
                      color="primary"
                      sx={{
                        "& .MuiBadge-badge": {
                          backgroundColor: "#4a8ecc",
                          color: "white",
                        },
                      }}
                    >
                      <DisplaySettingsIcon
                        fontSize="large"
                        style={{ color: "#def62d", fontSize: "2rem" }}
                      />
                    </Badge>

                    <div
                      className="group-name"
                      style={{
                        fontWeight: "bold",
                        textAlign: "center",
                        marginTop: "4px",
                      }}
                    >
                      {group.groupName}
                    </div>

                    <Chip
                      icon={koCount > 0 ? <ErrorIcon /> : <CheckCircleIcon />}
                      label={koCount > 0 ? `${koCount} KO` : "Tutto OK"}
                      size="small"
                      color={koCount > 0 ? "error" : "success"}
                      variant={koCount > 0 ? "filled" : "outlined"}
                      sx={{
                        height: 24,
                        fontWeight: "bold",
                        mt: 0.5,
                        background: koCount > 0 ? "red":"white"
                      }}
                    />
                  </Box>
                </div>
              );
            })}
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default GroupsSection;